import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Grid, CircularProgress, Button, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ClearIcon from '@mui/icons-material/Clear';
import CasinoIcon from '@mui/icons-material/Casino';
import useTeamCompetitionData from './useTeamCompetitionData';
import NavLink from './common/NavLink';
import { slugify } from '../utils/slugify';

const StandingsCalculator = () => {
  const { id } = useParams();
  const { 
    teams, 
    matches, 
    name, 
    group, 
    promotionSpots, 
    promotionPlayoffSpots, 
	relegationPlayoffSpots,
    relegationProbs, 
    loading, 
    error 
  } = useTeamCompetitionData(id);

  const [localMatches, setLocalMatches] = useState({});
  const [selectedRound, setSelectedRound] = useState(1);
  const [hoveredMatchTeams, setHoveredMatchTeams] = useState(null);
  const [hoveredTableTeamId, setHoveredTableTeamId] = useState(null);

  const leagueNameDisplay = useMemo(() => {
    if (name) return name;
    if (teams && teams.length > 0 && teams[0].competitionId) {
      return teams[0].competitionId; 
    }
    return 'Liga';
  }, [name, teams]);
  
  const getRelegationGradientColor = (value) => {
    if (!value || value <= 0) return 'transparent';
    const powerValue = Math.pow(value / 100, 1.0);
    const r = 255;
    const g = 47;
    const b = 47;
    const alpha = powerValue * 0.5;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const initializeMatches = () => {
    if (matches?.length > 0) {
      const initialMatchesState = {};
      matches.forEach(m => {
        const isPlanned = m.status && m.status.toLowerCase() === 'planned';
        initialMatchesState[m.matchId || m.id] = {
          homeGoals: isPlanned || m.homeGoals === null ? '' : Number(m.homeGoals),
          awayGoals: isPlanned || m.awayGoals === null ? '' : Number(m.awayGoals),
          homeTeamId: m.homeTeamId,
          awayTeamId: m.awayTeamId,
          round: Number(m.round || 1)
        };
      });
      setLocalMatches(initialMatchesState);

      const allRounds = [...new Set(Object.values(initialMatchesState).map(m => m.round))].sort((a, b) => a - b);
      
      const fullyCompletedRounds = allRounds.filter(r => {
        const roundMatches = Object.values(initialMatchesState).filter(m => m.round === r);
        if (roundMatches.length === 0) return false;
        return roundMatches.every(m => m.homeGoals !== '' && m.awayGoals !== '');
      });

      let defaultRound = allRounds[0] || 1;
      if (fullyCompletedRounds.length > 0) {
        const maxCompletedRound = Math.max(...fullyCompletedRounds);
        const maxIndex = allRounds.indexOf(maxCompletedRound);
        
        if (maxIndex !== -1 && maxIndex < allRounds.length - 1) {
          defaultRound = allRounds[maxIndex + 1];
        } else {
          defaultRound = maxCompletedRound;
        }
      }
      setSelectedRound(defaultRound);
    }
  };

  useEffect(() => {
    initializeMatches();
  }, [matches]);

  const handleGoalChange = (matchId, side, operation) => {
    setLocalMatches(prev => {
      const match = prev[matchId];
      let home = match.homeGoals;
      let away = match.awayGoals;

      if (home === '' || away === '') {
        if (operation === 'increment') {
          home = side === 'homeGoals' ? 1 : 0;
          away = side === 'awayGoals' ? 1 : 0;
        } else if (operation === 'decrement') {
          home = 0;
          away = 0;
        }
      } else {
        if (operation === 'increment') {
          if (side === 'homeGoals') home += 1;
          else away += 1;
        } else {
          if (side === 'homeGoals') home = Math.max(0, home - 1);
          else away = Math.max(0, away - 1);
        }
      }
      return { ...prev, [matchId]: { ...match, homeGoals: home, awayGoals: away } };
    });
  };

  const clearMatch = (matchId) => {
    setLocalMatches(prev => ({ ...prev, [matchId]: { ...prev[matchId], homeGoals: '', awayGoals: '' } }));
  };

  const randomizeMatches = (scope) => {
    setLocalMatches(prev => {
      const nextState = { ...prev };
      Object.keys(nextState).forEach(matchId => {
        const m = nextState[matchId];
        if (m.homeGoals === '' && m.awayGoals === '') {
          if (scope === 'season' || (scope === 'round' && m.round === selectedRound)) {
            nextState[matchId] = {
              ...m,
              homeGoals: Math.floor(Math.random() * 4),
              awayGoals: Math.floor(Math.random() * 4)
            };
          }
        }
      });
      return nextState;
    });
  };

  const calculatedTable = useMemo(() => {
    if (!teams?.length) return [];
    const stats = {};
    
    teams.forEach(t => {
      const tId = t.teamId || t.id;
      const penalty = t.pointsPenalty ? Number(t.pointsPenalty) : 0;
      
      stats[tId] = { 
        teamId: tId, 
        teamName: t.teamName || t.name, 
        points: 0 - penalty, 
        goalsScored: 0, 
        goalsConceded: 0, 
        matchesPlayed: 0 
      };
    });

    Object.values(localMatches).forEach(m => {
      if (m.homeGoals !== '' && m.awayGoals !== '' && stats[m.homeTeamId] && stats[m.awayTeamId]) {
        const hG = Number(m.homeGoals), aG = Number(m.awayGoals);
        stats[m.homeTeamId].matchesPlayed += 1; stats[m.awayTeamId].matchesPlayed += 1;
        stats[m.homeTeamId].goalsScored += hG; stats[m.homeTeamId].goalsConceded += aG;
        stats[m.awayTeamId].goalsScored += aG; stats[m.awayTeamId].goalsConceded += hG;

        if (hG > aG) stats[m.homeTeamId].points += 3;
        else if (hG < aG) stats[m.awayTeamId].points += 3;
        else { stats[m.homeTeamId].points += 1; stats[m.awayTeamId].points += 1; }
      }
    });

    const teamsArray = Object.values(stats);
    teamsArray.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      const tiedTeams = teamsArray.filter(t => t.points === a.points);
      
      if (tiedTeams.length >= 2) {
        const tiedIds = tiedTeams.map(t => t.teamId);
        const miniStats = {};
        tiedIds.forEach(id => miniStats[id] = { points: 0, goalsScored: 0, goalsConceded: 0 });

        Object.values(localMatches).forEach(m => {
          if (m.homeGoals !== '' && m.awayGoals !== '' && tiedIds.includes(m.homeTeamId) && tiedIds.includes(m.awayTeamId)) {
            const hG = Number(m.homeGoals), aG = Number(m.awayGoals);
            miniStats[m.homeTeamId].goalsScored += hG; miniStats[m.homeTeamId].goalsConceded += aG;
            miniStats[m.awayTeamId].goalsScored += aG; miniStats[m.awayTeamId].goalsConceded += hG;
            if (hG > aG) miniStats[m.homeTeamId].points += 3;
            else if (hG < aG) miniStats[m.awayTeamId].points += 3;
            else { miniStats[m.homeTeamId].points += 1; miniStats[m.awayTeamId].points += 1; }
          }
        });

        if (miniStats[b.teamId].points !== miniStats[a.teamId].points) return miniStats[b.teamId].points - miniStats[a.teamId].points;
        const diffA_mini = miniStats[a.teamId].goalsScored - miniStats[a.teamId].goalsConceded;
        const diffB_mini = miniStats[b.teamId].goalsScored - miniStats[b.teamId].goalsConceded;
        if (diffB_mini !== diffA_mini) return diffB_mini - diffA_mini;
        if (miniStats[b.teamId].goalsScored !== miniStats[a.teamId].goalsScored) return miniStats[b.teamId].goalsScored - miniStats[a.teamId].goalsScored;
      }

      const diffA_global = a.goalsScored - a.goalsConceded;
      const diffB_global = b.goalsScored - b.goalsConceded;
      if (diffB_global !== diffA_global) return diffB_global - diffA_global;
      return b.goalsScored - a.goalsScored;
    });

    return teamsArray;
  }, [localMatches, teams]);

  const rounds = useMemo(() => [...new Set(Object.values(localMatches).map(m => m.round))].sort((a, b) => a - b), [localMatches]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" align="center">Błąd ładowania danych.</Typography>;

	const pageTitle = `Kalkulator tabeli - ${leagueNameDisplay}${group && group.trim() !== '' ? ` (${group})` : ''}`;
	
  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
      <Helmet>
        <title>{pageTitle} | Symuliga</title>
      </Helmet>

      <Typography variant="h5" component="h1" sx={{ mb: 1, fontWeight: 'bold', color: '#fff', textAlign: 'center' }}>
        {pageTitle}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 3, color: '#aaa', textAlign: 'center', maxWidth: 800, margin: '0 auto 24px auto', lineHeight: 1.5 }}>
        Wpisz wyniki nadchodzących spotkań. Tabela po prawej stronie obliczy się automatycznie w czasie rzeczywistym.
      </Typography>

      <Grid container spacing={4} alignItems="flex-start">
        
        {/* LEWA STRONA: KOLEJKI I MECZE */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.8, color: '#aaa', fontSize: '0.8rem' }}>Wybierz kolejkę:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
              {rounds.map(r => (
                <Button 
                  key={r} 
                  variant={selectedRound === r ? "contained" : "outlined"} 
                  onClick={() => setSelectedRound(r)} 
                  sx={{ minWidth: '30px', height: '26px', p: 0, borderRadius: 1, fontSize: '0.78rem' }}
                >
                  {r}
                </Button>
              ))}
            </Box>
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold', color: '#1976d2' }}>
            Mecze Kolejki {selectedRound}
          </Typography>
          
          <Grid container spacing={1}>
            {Object.entries(localMatches).filter(([_, m]) => m.round === selectedRound).map(([id, m]) => {
              const isMatchHighlightedFromTable = hoveredTableTeamId && (m.homeTeamId === hoveredTableTeamId || m.awayTeamId === hoveredTableTeamId);
              
              const homeTeamName = teams.find(t => (t.teamId || t.id) === m.homeTeamId)?.teamName || 'Gospodarz';
              const awayTeamName = teams.find(t => (t.teamId || t.id) === m.awayTeamId)?.teamName || 'Gość';

              return (
                <Grid item xs={12} key={id}>
                  <Paper 
                    onMouseEnter={() => setHoveredMatchTeams([m.homeTeamId, m.awayTeamId])}
                    onMouseLeave={() => setHoveredMatchTeams(null)}
                    sx={{ 
                      backgroundColor: isMatchHighlightedFromTable ? '#393939' : '#2c2c2c', 
                      px: 1.5, 
                      py: 0.4, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      borderRadius: 1.5, 
                      border: '1px solid #3a3a3a',
                      transition: 'none',
                      '&:hover': { backgroundColor: '#333' }
                    }}
                  >
                    <Typography 
                      component={NavLink}
                      to={`/team/${m.homeTeamId}/${slugify(homeTeamName)}`}
                      sx={{ flex: 1, fontSize: '0.82rem', fontWeight: 500, color: '#fff', pr: 1, noWrap: true, textDecoration: 'none', '&:hover': { textDecoration: 'underline', color: '#fff' } }}
                    >
                      {homeTeamName}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#1e1e1e', borderRadius: 1, p: '2px 4px' }}>
                      <IconButton size="small" sx={{ color: '#888', p: 0.2 }} onClick={() => handleGoalChange(id, 'homeGoals', 'decrement')}><RemoveIcon fontSize="inherit" /></IconButton>
                      <Typography sx={{ fontWeight: 'bold', width: '18px', textAlign: 'center', color: '#fff', fontSize: '0.85rem' }}>{m.homeGoals}</Typography>
                      <IconButton size="small" sx={{ color: '#fff', p: 0.2 }} onClick={() => handleGoalChange(id, 'homeGoals', 'increment')}><AddIcon fontSize="inherit" /></IconButton>
                      
                      <Typography sx={{ mx: 0.3, color: '#777', fontSize: '0.85rem', fontWeight: 'bold' }}>:</Typography>
                      
                      <IconButton size="small" sx={{ color: '#888', p: 0.2 }} onClick={() => handleGoalChange(id, 'awayGoals', 'decrement')}><RemoveIcon fontSize="inherit" /></IconButton>
                      <Typography sx={{ fontWeight: 'bold', width: '18px', textAlign: 'center', color: '#fff', fontSize: '0.85rem' }}>{m.awayGoals}</Typography>
                      <IconButton size="small" sx={{ color: '#fff', p: 0.2 }} onClick={() => handleGoalChange(id, 'awayGoals', 'increment')}><AddIcon fontSize="inherit" /></IconButton>
                    </Box>
                    
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pl: 1 }}>
                      <Typography 
                        component={NavLink}
                        to={`/team/${m.awayTeamId}/${slugify(awayTeamName)}`}
                        sx={{ textAlign: 'right', fontSize: '0.82rem', fontWeight: 500, color: '#fff', noWrap: true, mr: 1, textDecoration: 'none', '&:hover': { textDecoration: 'underline', color: '#fff' } }}
                      >
                        {awayTeamName}
                      </Typography>
                      
                      <IconButton size="small" onClick={() => clearMatch(id)} sx={{ color: '#666', p: 0.2, '&:hover': { color: '#f44336' } }}>
                        <ClearIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* AKCJE MASOWE */}
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Przypisuje drużynom losową ilość bramek od 0 do 3" placement="top">
                <span>
				  <Button variant="outlined" color="info" size="small" startIcon={<CasinoIcon />} onClick={() => randomizeMatches('round')} sx={{ textTransform: 'none' }}>
					Wylosuj mecze tej kolejki
				  </Button>
				 </span>
              </Tooltip>
              
              <Tooltip title="Przypisuje drużynom losową ilość bramek od 0 do 3" placement="top">
                <span>
                  <Button variant="outlined" color="secondary" size="small" startIcon={<CasinoIcon />} onClick={() => randomizeMatches('season')} sx={{ textTransform: 'none' }}>
                    Wylosuj wszystkie mecze
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <Button variant="text" color="error" size="small" startIcon={<RestartAltIcon />} onClick={initializeMatches} sx={{ textTransform: 'none', fontWeight: 'bold', alignSelf: 'flex-start' }}>
              Resetuj wszystko do stanu z bazy
            </Button>
          </Box>
        </Grid>

        {/* PRAWA STRONA: TABELA LIVE */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
          <Box sx={{ width: '100%', maxWidth: { md: 'max-content' } }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold', color: '#888', pl: { lg: 1 } }}>
              Kalkulator tabeli
            </Typography>
            
            <TableContainer component={Paper} sx={{ width: '100%', backgroundColor: '#1e1e1e', borderRadius: 2, border: '1px solid #333' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { color: '#888', fontWeight: 'bold', borderBottom: '2px solid #333', backgroundColor: '#252525', fontSize: '0.8rem' } }}>
                    <TableCell align="center" sx={{ width: 18, px: 0.3 }}>#</TableCell>
                    <TableCell sx={{ pl: 1 }}>Drużyna</TableCell>
                    <TableCell align="center" sx={{ width: 35, px: 1 }}>M</TableCell>
                    <TableCell align="center" sx={{ width: 45, px: 1 }}>Pkt</TableCell>
                    <TableCell align="center" sx={{ width: 70, px: 1 }}>Bramki</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calculatedTable.map((row, i) => {
                    const position = i + 1;
                    const totalTeams = calculatedTable.length;
                    const isHoveredFromMatch = hoveredMatchTeams && hoveredMatchTeams.includes(row.teamId);
                    
                    const prom = Number(promotionSpots || 0);
                    const promPlayoff = Number(promotionPlayoffSpots || 0);
                    const relegPlayoff = Number(relegationPlayoffSpots || 0);

                    // 1. Najpierw wyliczamy, ile drużyn jest już pewnych spadku (mają 100.0%)
					const confirmedRelegations = Object.values(relegationProbs).filter(val => val === 100.0).length;

					// 2. Sprawdzamy czy mamy kompletne dane (wszystkie drużyny mają przypisane 100.0 lub 0.0)
					const allRelegationDefined = Object.values(relegationProbs).every(val => val === 100.0 || val === 0.0);

					// 3. Logika kolorowania
					let rowBgColor = 'transparent';

					if (prom > 0 && i < prom) {
						rowBgColor = 'rgba(46, 125, 50, 0.3)'; // awans
					} else if (promPlayoff > 0 && i < (prom + promPlayoff)) {
						rowBgColor = 'rgba(150, 200, 80, 0.2)'; // baraże o awans
					} else if (allRelegationDefined) {
						// Jeśli sytuacja jest jasna, kolorujemy na podstawie wyliczeń:
						// Spadek bezpośredni: ostatnie 'confirmedRelegations' miejsc
						if (i >= (totalTeams - confirmedRelegations)) {
							rowBgColor = 'rgba(255, 69, 58, 0.3)'; 
						} 
						// Baraże o utrzymanie: tuż nad spadkiem
						else if (relegPlayoff > 0 && i >= (totalTeams - confirmedRelegations - relegPlayoff)) {
							rowBgColor = 'rgba(255, 152, 0, 0.3)';
						}
					} else {
						// Jeśli sytuacja jest niepewna, używamy gradientu z prawdopodobieństw
						if (relegationProbs && relegationProbs[position]) {
							rowBgColor = getRelegationGradientColor(relegationProbs[position]);
						}
					}

                

                    return (
                      <TableRow 
                        key={i} 
                        onMouseEnter={() => setHoveredTableTeamId(row.teamId)}
                        onMouseLeave={() => setHoveredTableTeamId(null)}
                        sx={{
						  backgroundColor: rowBgColor,
						  boxShadow: isHoveredFromMatch ? 'inset 0 0 8px rgba(255, 255, 255, 0.3)' : 'none',
						  //transition: 'box-shadow 0.2s ease',
						  '& td': { py: 0.6, fontSize: '0.82rem'}
						}}
                      >
                        <TableCell align="center" sx={{ color: '#fff', fontWeight: i < 3 ? 'bold' : 'normal', px: 0.3 }}>{i + 1}</TableCell>
                        
                        <TableCell 
                          component={NavLink}
                          to={`/team/${row.teamId}/${slugify(row.teamName)}`}
                          sx={{ 
                            color: '#fff', 
                            fontWeight: 500, 
                            pr: 3, 
                            pl: 1, 
                            textDecoration: 'none',
                            cursor: 'pointer',
                            '&:hover': { color: '#fff', textDecoration: 'underline' } 
                          }}
                        >
                          {row.teamName}
                        </TableCell>
                        
                        <TableCell align="center" sx={{ color: '#aaa', px: 1 }}>{row.matchesPlayed}</TableCell>
                        <TableCell align="center" sx={{ color: '#1976d2', fontWeight: 'bold', fontSize: '0.9rem', px: 1 }}>{row.points}</TableCell>
                        <TableCell align="center" sx={{ color: '#aaa', px: 1 }}>{row.goalsScored}:{row.goalsConceded}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>

      </Grid>
    </Box>
  );
};

export default StandingsCalculator;